// The category drawer on the Budget tab: name, plan mode, amount (or items and room for other
// spending), group and delete, saved together with one "Save changes". No spending details: those
// are on the Overview category screen. The drawer edits a draft; nothing is saved until Save.
import type { CategoryDto, OverviewDto } from '../../../shared/api';
import { esc, money, parseCents } from '../format';
import { AUTO_LOG_LABEL, AUTO_LOG_WHAT, categoryItems } from './items';

export const PLAN_MODE_QUESTION = 'How do you plan this category?';
export const PLAN_MODE_HINT = 'One amount: you set a single budget. List of items: the budget is the total of the items you add.';
/** Money set aside: the same choice, in transfers. */
export const PLAN_MODE_HINT_SET_ASIDE = 'One amount: you set a single amount to move and mark it done. List of transfers: the plan is the total of the transfers you add.';
export const NOTE_LABEL = 'Note';
export const NOTE_MAX = 2000;
export const ROOM_LABEL = 'Room for other spending';
export const ROOM_HINT = "For purchases that don't match an item above, like a surprise repair.";
export const ADD_ROOM = 'Add room for other spending';
export const DISCARD_QUESTION = 'Discard changes?';
export const PURCHASES_LINK = "See this category's purchases";
export const SAME_AMOUNT_LABEL = 'Same amount every month';
export const SAME_AMOUNT_HINT = 'For bills that never change, like insurance or a car payment. New purchases here start at this amount.';
export const SET_AMOUNT_FIRST = 'Set a planned amount first.';
export const SAME_AMOUNT_OFF = 'Same amount every month turns off.';

/** What the drawer is editing. Amounts are kept as typed until Save. */
export interface Draft {
  name: string;
  itemized: boolean;
  /** One amount: the planned amount. */
  amount: string;
  /** List of items: room for other spending. */
  room: string;
  /** The room line shows once it is above $0 or "Add room for other spending" was tapped. */
  roomShown: boolean;
  groupId: string;
  /** The category's note, as typed (line breaks kept). */
  note: string;
  /** "Same amount every month" (one amount in a spending group). */
  sameAmount: boolean;
  /** Its due day ('' for any day). */
  dueDay: string;
  autoLog: boolean;
}

const dollars = (cents: number) => String(cents / 100);

export function draftFrom(k: CategoryDto): Draft {
  return {
    name: k.name,
    itemized: k.itemized,
    amount: dollars(k.budgetCents),
    room: dollars(k.extraCents),
    roomShown: k.extraCents > 0,
    groupId: k.groupId ?? '',
    note: k.note ?? '',
    sameAmount: k.sameAmount,
    dueDay: k.billDueDay === null ? '' : String(k.billDueDay),
    autoLog: k.billAutoLog,
  };
}

/** The room a list starts with: what it had before (remembered), else the one amount. Money set aside has none. */
export const startingRoom = (k: CategoryDto) => (k.kind === 'set_aside' ? 0 : k.itemized ? k.extraCents : (k.savedExtraCents ?? k.budgetCents));

/** Switch the draft's plan mode, filling in the amount the other mode starts with (as saving would). */
export function switchMode(k: CategoryDto, draft: Draft, itemized: boolean): Draft {
  if (draft.itemized === itemized) return draft;
  if (itemized === k.itemized) {
    // Back to the saved mode: its saved amounts.
    const saved = draftFrom(k);
    return { ...draft, itemized, amount: saved.amount, room: saved.room, roomShown: saved.roomShown, sameAmount: saved.sameAmount, autoLog: saved.autoLog };
  }
  if (itemized) {
    // A list of items is never "same amount every month".
    const room = startingRoom(k);
    return { ...draft, itemized, room: dollars(room), roomShown: room > 0, sameAmount: false, autoLog: false };
  }
  return { ...draft, itemized, amount: dollars(k.budgetCents) };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** Dollars, with cents only when there are some: "$800", "$36.48". */
const amount = (c: number) => money(c, c % 100 ? 2 : 0);

/** One sentence, shown before Save, on what switching plan mode does. Empty when the mode is unchanged. */
export function modeSwitchText(d: OverviewDto, k: CategoryDto, draft: Draft): string {
  if (draft.itemized === k.itemized) return '';
  const n = k.itemized ? categoryItems(d, k.id).length : k.keptItemCount;
  const what = k.kind === 'set_aside' ? ['transfer', 'transfers'] : ['item', 'items'];
  if (draft.itemized) {
    const room = parseCents(draft.room);
    const kept = n ? ` (the ${plural(n, what[0], what[1])} from before come back)` : '';
    const extra = draft.roomShown && room > 0 ? `, plus ${amount(room)} room for other spending` : '';
    return `When you save, the budget becomes the total of its ${what[1]}${kept}${extra}.${k.sameAmount ? ` ${SAME_AMOUNT_OFF}` : ''}`;
  }
  return `When you save, the budget becomes one amount of ${amount(Math.max(0, parseCents(draft.amount)) || 0)}${n ? `, and its ${plural(n, what[0], what[1])} are kept in case you switch back` : ''}.`;
}

const cents = (v: string) => (v.trim() === '' ? 0 : parseCents(v));

/** Unsaved edits? (Amounts compare as cents, so "800" and "800.00" are the same.) */
export function isDirty(k: CategoryDto, draft: Draft): boolean {
  if (draft.name.trim() !== k.name || draft.itemized !== k.itemized || draft.groupId !== (k.groupId ?? '')) return true;
  if (cleanNote(draft.note) !== (k.note ?? null)) return true;
  if (draft.sameAmount !== k.sameAmount) return true;
  if (draft.sameAmount && (draft.dueDay !== (k.billDueDay === null ? '' : String(k.billDueDay)) || draft.autoLog !== k.billAutoLog)) return true;
  return draft.itemized ? cents(draft.room) !== k.extraCents : cents(draft.amount) !== k.budgetCents;
}

export type DrawerPatch = Partial<{
  name: string;
  itemized: boolean;
  groupId: string;
  budgetCents: number;
  extraCents: number;
  note: string | null;
  sameAmount: boolean;
  billDueDay: number | null;
  billAutoLog: boolean;
}>;

/** A note as saved: blank space around it trimmed, line breaks kept; empty is no note. */
export const cleanNote = (note: string) => note.replace(/\r\n/g, '\n').trim() || null;

/** The one PATCH "Save changes" sends (changed fields only), or the reason it cannot be saved. */
export function drawerPatch(d: OverviewDto, k: CategoryDto, draft: Draft): { patch: DrawerPatch } | { error: string } {
  const name = draft.name.trim();
  if (!name) return { error: 'Give the category a name.' };
  const patch: DrawerPatch = {};
  if (name !== k.name) patch.name = name;
  if (draft.groupId && draft.groupId !== (k.groupId ?? '')) patch.groupId = draft.groupId;
  if (draft.itemized !== k.itemized) patch.itemized = draft.itemized;
  const note = cleanNote(draft.note);
  if (note && note.length > NOTE_MAX) return { error: `Keep the note to ${NOTE_MAX} characters.` };
  if (note !== (k.note ?? null)) patch.note = note;
  if (draft.itemized) {
    // Money set aside is planned by its transfers alone.
    if (!groupIsSetAside(d, draft, k)) {
      const room = cents(draft.room);
      if (!(room >= 0)) return { error: 'Enter an amount for room for other spending, or 0.' };
      if (room !== k.extraCents || patch.itemized) patch.extraCents = room;
    }
  } else {
    const amount = cents(draft.amount);
    if (!(amount >= 0)) return { error: 'Enter an amount, or 0.' };
    if (amount !== k.budgetCents || patch.itemized === false) patch.budgetCents = amount;
    // "Same amount every month": spending groups only, with an amount; auto-log needs a due day.
    const same = draft.sameAmount && !groupIsSetAside(d, draft, k);
    if (same && amount <= 0) return { error: SET_AMOUNT_FIRST };
    if (same !== k.sameAmount) patch.sameAmount = same;
    if (same) {
      const due = draft.dueDay ? Number(draft.dueDay) : null;
      const auto = draft.autoLog && due !== null;
      if (due !== k.billDueDay || patch.sameAmount) patch.billDueDay = due;
      if (auto !== k.billAutoLog || patch.sameAmount) patch.billAutoLog = auto;
    }
  }
  return { patch };
}

/** Whether the category will be money set aside: the kind of the group picked in the draft. */
const groupIsSetAside = (d: OverviewDto, draft: Draft, k: CategoryDto) =>
  (d.groups.find((g) => g.id === draft.groupId)?.kind ?? k.kind) === 'set_aside';

/** Item rows inside the drawer: name (opens the item editor) and amount, or "Last month". */
function itemRows(d: OverviewDto, k: CategoryDto): string {
  return categoryItems(d, k.id)
    .map(
      (i) =>
        `<button class="brow bitem drow" data-a="drawer-item" data-id="${esc(i.id)}"><span class="clamp">${esc(i.name)}</span><span class="${i.expectedMode === 'last_month' ? 'muted' : ''}">${
          i.expectedMode === 'last_month' ? 'Last month' : money(i.amountCents, 2)
        }</span></button>`,
    )
    .join('');
}

/**
 * "Same amount every month" under the planned amount (one amount, spending): a switch, and when on
 * the due day and auto-log. Disabled at $0 with "Set a planned amount first".
 */
export function sameAmountFields(draft: Draft): string {
  const zero = !(cents(draft.amount) > 0);
  const on = draft.sameAmount && !zero;
  const days = `<option value="">Any day</option>${Array.from({ length: 31 }, (_, i) => i + 1)
    .map((n) => `<option value="${n}" ${draft.dueDay === String(n) ? 'selected' : ''}>Day ${n}</option>`)
    .join('')}`;
  const anyDay = draft.dueDay === '';
  return `<label class="check"><input type="checkbox" role="switch" id="dr-same" ${on ? 'checked' : ''} ${zero ? 'disabled' : ''}> ${SAME_AMOUNT_LABEL}</label>
  <p class="hint" id="dr-same-hint">${esc(zero ? SET_AMOUNT_FIRST : SAME_AMOUNT_HINT)}</p>
  ${
    on
      ? `<label class="fld"><span>Due</span><select id="dr-due" class="sel">${days}</select></label>
  <label class="check"><input type="checkbox" id="dr-auto" ${draft.autoLog && !anyDay ? 'checked' : ''} ${anyDay ? 'disabled' : ''}> ${AUTO_LOG_LABEL}</label>
  <p class="hint">${esc(AUTO_LOG_WHAT)}</p>`
      : ''
  }`;
}

/** The drawer. `confirm` shows "Discard changes?" in place of the buttons. */
export function vCategoryDrawer(d: OverviewDto, k: CategoryDto, draft: Draft, opts: { confirm?: boolean; error?: string } = {}): string {
  const setAside = groupIsSetAside(d, draft, k);
  const listLabel = setAside ? 'List of transfers' : 'List of items';
  const seg = (on: boolean, mode: 'one' | 'list', label: string) =>
    `<button type="button" role="radio" aria-checked="${on}" data-a="drawer-mode" data-mode="${mode}">${label}</button>`;
  const switchLine = modeSwitchText(d, k, draft);
  const items = itemRows(d, k);
  const addLabel = setAside ? 'Add transfer' : 'Add item';
  const room = setAside
    ? ''
    : draft.roomShown
      ? `<div class="brow bitem droom"><label for="dr-room">${ROOM_LABEL}</label><div class="money"><span>$</span><input id="dr-room" inputmode="decimal" value="${esc(draft.room)}"></div></div>
  <p class="hint">${esc(ROOM_HINT)}</p>`
      : `<button class="brow bitem" data-a="drawer-room"><span>${ADD_ROOM}</span><span>+</span></button>`;
  const total = categoryItems(d, k.id).reduce((s, i) => s + (i.month?.expectedCents ?? 0), 0) + (setAside || !draft.roomShown ? 0 : Math.max(0, cents(draft.room) || 0));
  const plan = draft.itemized
    ? `<div class="list">${items}<button class="brow bitem" data-a="drawer-item-add"><span>${addLabel}</span><span>+</span></button>
  ${room}
  <div class="btotal"><span>Total this month</span><span id="dr-total">${money(total)}</span></div></div>`
    : `<label class="fld"><span>Planned amount</span><div class="amt"><b>$</b><input id="dr-amt" inputmode="decimal" placeholder="0" value="${esc(draft.amount)}"></div></label>
  ${setAside ? '' : sameAmountFields(draft)}`;
  const used = d.items.some((i) => i.categoryId === k.id) || d.transactions.some((t) => t.categoryId === k.id);
  const del = used
    ? `<p class="hint" id="cat-del-why">It still has purchases or items. Move or delete them first, then delete the category.</p>`
    : `<p class="hint" id="cat-del-why" hidden></p><div class="acts"><button class="btn danger" data-a="cat-delete" data-id="${esc(k.id)}">Delete category</button></div>`;
  const buttons = opts.confirm
    ? `<div class="discard" role="alertdialog" aria-label="${DISCARD_QUESTION}"><p><b>${DISCARD_QUESTION}</b></p><div class="btns"><button class="btn" data-a="drawer-keep">Keep editing</button><button class="btn danger" data-a="drawer-discard">Discard</button></div></div>`
    : `<div class="btns"><button class="btn primary" data-a="drawer-save">Save changes</button><button class="btn" data-a="close">Cancel</button></div>`;
  return `<div class="grab"></div><h2>Edit category</h2>
  <input type="hidden" id="dr-id" value="${esc(k.id)}">
  <label class="fld"><span>Name</span><input id="dr-name" type="text" maxlength="60" value="${esc(draft.name)}"></label>
  <div class="fld"><span>${PLAN_MODE_QUESTION}</span><div class="seg" role="radiogroup" aria-label="${PLAN_MODE_QUESTION}">${seg(!draft.itemized, 'one', 'One amount')}${seg(draft.itemized, 'list', listLabel)}</div></div>
  <p class="hint">${esc(setAside ? PLAN_MODE_HINT_SET_ASIDE : PLAN_MODE_HINT)}</p>
  ${switchLine ? `<p class="hint switchnote" id="dr-switch">${esc(switchLine)}</p>` : ''}
  ${plan}
  <label class="fld"><span>${NOTE_LABEL}</span><textarea id="dr-note" class="catnote-in" maxlength="${NOTE_MAX}" rows="4" placeholder="Anything to remember about this category">${esc(draft.note)}</textarea></label>
  <label class="fld"><span>Group</span><select id="dr-group" class="sel">${d.groups
    .map((g) => `<option value="${esc(g.id)}" ${g.id === draft.groupId ? 'selected' : ''}>${esc(g.name)}${g.kind === 'set_aside' ? ' (money set aside)' : ''}</option>`)
    .join('')}</select></label>
  ${del}
  <p class="err" id="f-err">${opts.error ? esc(opts.error) : ''}</p>
  ${buttons}
  <button class="linkish" data-a="drawer-purchases">${PURCHASES_LINK}</button>`;
}
